import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, FileText, Calendar, Home, LucideIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../contexts/UserContext';
import { cn } from '../../utils/cn';
import { getResidenceTypeLabel } from '../../utils/residence';

export interface StyretNavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

export function getStyretNavItems(residenceId?: number, residenceType?: 'condo' | 'cooperative' | null): StyretNavItem[] {
  return [
    {
      name: 'Oversikt',
      href: '/styret/oversikt',
      icon: LayoutDashboard
    },
    {
      name: getResidenceTypeLabel(residenceType),
      href: `/styret/bofellesskap/${residenceId || ''}`,
      icon: Building2
    },
    {
      name: 'Styret',
      href: '/styret/styret',
      icon: Users
    },
    {
      name: 'Beboere',
      href: `/styret/beboere/${residenceId || ''}`,
      icon: Users
    },
    {
      name: 'Boliger',
      href: `/styret/boliger/${residenceId || ''}`,
      icon: Home
    },
    {
      name: 'Reservasjoner',
      href: '/styret/reservasjoner',
      icon: Calendar
    },
    {
      name: 'Dokumenter',
      href: '/styret/dokumenter',
      icon: FileText
    }
  ];
}

export function StyretSidebar() {
  const { currentResidence } = useUser();
  const { pathname } = useLocation();
  const [requestCount, setRequestCount] = useState<number>(0);

  useEffect(() => {
    const residenceId = currentResidence?.residence_id;
    if (!residenceId) {
      setRequestCount(0);
      return;
    }
    let isMounted = true;
    const fetchCount = async () => {
      const { count, error } = await supabase
        .from('residence_invites')
        .select('id', { count: 'exact', head: true })
        .eq('residence_id', residenceId)
        .eq('status', 'requested');
      if (!error && isMounted) {
        setRequestCount(count || 0);
      }
    };
    fetchCount();

    // Optional: subscribe to realtime if enabled later
    return () => { isMounted = false; };
  }, [currentResidence?.residence_id]);

  const navItems = getStyretNavItems(currentResidence?.residence_id, currentResidence?.residence?.type);

  return (
    <nav className="hidden lg:flex w-48 2xl:w-64 shadow-lg flex-col z-30 bg-base-white fixed top-16 left-0 bottom-0 border-r border-gray-200">
      <div className="absolute inset-0 bg-base-white -z-10" />
      
      <div className="flex flex-col h-full">
        <div className="flex-1 p-4 xl:p-4 2xl:p-6 pt-6 overflow-y-auto">
          <div className="space-y-2">
            {navItems.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 xl:px-3 2xl:px-4 py-3 transition-all duration-200',
                    isActive 
                      ? 'bg-condo-light text-condo-dark shadow-sm border border-condo-med/20' 
                      : 'hover:bg-light-1 text-gray-700 hover:text-condo-dark hover:shadow-sm'
                  )}
                >
                  <item.icon className={`h-[18px] w-[18px] ${isActive ? 'text-condo-dark' : 'text-gray-600'}`} />
                  <span className="flex-1 text-sm font-medium">{item.name}</span>
                  {item.name === 'Beboere' && requestCount > 0 && (
                    <span className="ml-auto inline-flex items-center justify-center rounded-full bg-red-600 text-white text-xs px-2 py-0.5 min-w-[1.25rem] font-medium">
                      {requestCount}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
} 